import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ITEM_CONDITIONS,
  type InventoryCheckoutResponse,
  type ItemCondition,
} from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { inventoryApi } from '@/services/inventory.api';
import { ApiError } from '@/services/api-client';
import { formatDate } from '@tabita/i18n';
import { Alert, Button, EmptyState, Spinner } from '@/components/ui';

/**
 * Every piece of equipment currently out, across the whole catalog — the
 * question "what do we have on loan right now" that a per-item page cannot
 * answer on its own.
 */
export function CheckoutsListPage() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [overdueOnly, setOverdueOnly] = useState(false);

  const query = useQuery({
    queryKey: ['inventory', 'checkouts', 'all', { overdueOnly }],
    queryFn: ({ signal }) =>
      inventoryApi.listCheckouts(
        {
          status: overdueOnly ? undefined : 'OUT',
          overdueOnly: overdueOnly || undefined,
          limit: 100,
        },
        signal,
      ),
  });

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ['inventory', 'checkouts'] });

  const returnCheckout = useMutation({
    mutationFn: (input: { checkoutId: string; returnCondition: ItemCondition }) =>
      inventoryApi.returnCheckout(input.checkoutId, { returnCondition: input.returnCondition }),
    onSuccess: refresh,
  });

  const reportLost = useMutation({
    mutationFn: (checkoutId: string) => inventoryApi.reportLostCheckout(checkoutId, {}),
    onSuccess: refresh,
  });

  const checkouts = query.data?.data ?? [];

  return (
    <div className="page">
      <Link to="/inventory" className="back-link">
        {t('common.actions.back')}
      </Link>

      <header className="page-header">
        <h1 className="page-title">{t('inventory.checkout.title')}</h1>
      </header>

      <div className="filters">
        <label className="field-checkbox">
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={(event) => setOverdueOnly(event.target.checked)}
          />
          {t('inventory.checkout.overdueOnly')}
        </label>
      </div>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && checkouts.length === 0 && <EmptyState title={t('common.states.empty')} />}

      {checkouts.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('inventory.item.singular')}</th>
                <th scope="col">{t('people.person.singular')}</th>
                <th scope="col">{t('inventory.checkout.checkedOutAt')}</th>
                <th scope="col">{t('inventory.checkout.dueAt')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {checkouts.map((checkout) => (
                <CheckoutRow
                  key={checkout.id}
                  checkout={checkout}
                  onReturn={(returnCondition) =>
                    returnCheckout.mutate({ checkoutId: checkout.id, returnCondition })
                  }
                  onReportLost={() => reportLost.mutate(checkout.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CheckoutRow({
  checkout,
  onReturn,
  onReportLost,
}: {
  checkout: InventoryCheckoutResponse;
  onReturn: (returnCondition: ItemCondition) => void;
  onReportLost: () => void;
}) {
  const { t, locale } = useI18n();
  const [condition, setCondition] = useState<ItemCondition>('GOOD');

  return (
    <tr data-archived={checkout.isOverdue ? true : undefined}>
      <td>
        <Link to={`/inventory/${checkout.itemId}`} className="person-name">
          {checkout.itemName}
        </Link>
      </td>
      <td>
        <Link to={`/people/${checkout.borrowerPersonId}`} className="person-name">
          {checkout.borrowerDisplayName}
        </Link>
      </td>
      <td>{formatDate(checkout.checkedOutAt, locale)}</td>
      <td>
        {checkout.dueAt ? (
          formatDate(checkout.dueAt, locale)
        ) : (
          <span className="cell-empty">—</span>
        )}
        {checkout.isOverdue && (
          <span className="cell-flag"> · {t('inventory.checkout.overdue')}</span>
        )}
      </td>
      <td>
        {checkout.status === 'OUT' && (
          <div className="inline-confirm">
            <select
              aria-label={t('inventory.checkout.returnCondition')}
              className="field-input"
              value={condition}
              onChange={(event) => setCondition(event.target.value as ItemCondition)}
            >
              {ITEM_CONDITIONS.map((value) => (
                <option key={value} value={value}>
                  {t(`inventory.condition.${value}`)}
                </option>
              ))}
            </select>
            <Button variant="secondary" onClick={() => onReturn(condition)}>
              {t('inventory.checkout.return')}
            </Button>
            <Button variant="ghost" onClick={onReportLost}>
              {t('inventory.checkout.reportLost')}
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}
